import { formatDate } from '../../../shared/utils/localization';
import type { RosterCellEntry, RosterScheduleRule, RosterWeekDay } from '../types/Rostering';
import type { RosterDateLabelTranslator } from './rosterDateLabels';
import {
  formatMonthDayColumnLabel,
  formatWeekDayColumnLabel,
  MONDAY_FIRST_WEEKDAY_KEYS,
  MONTH_I18N_KEYS,
} from './rosterDateLabels';

const buildDayHeaderParts = (
  date: Date,
  weekdayIndex: number,
  t?: RosterDateLabelTranslator,
): Pick<RosterWeekDay, 'weekdayLabel' | 'dayNumberLabel' | 'monthLabel'> => ({
  weekdayLabel: t
    ? t(`weekdays.${MONDAY_FIRST_WEEKDAY_KEYS[weekdayIndex]}`)
    : formatDate(date, { weekday: 'short' }),
  dayNumberLabel: String(date.getDate()),
  monthLabel: t
    ? t(`monthsShort.${MONTH_I18N_KEYS[date.getMonth()]}`)
    : formatDate(date, { month: 'short' }),
});

const DAY_NAME_TO_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const startOfWeekMonday = (date: Date): Date => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const dayOffset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dayOffset);
  return start;
};

export const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

export const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const startOfMonth = (date: Date): Date => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  return start;
};

export const addMonths = (date: Date, months: number): Date => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
};

export type MonthCalendarDay = {
  date: Date;
  dayKey: string;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isInSelectedWeek: boolean;
  isInSelectedMonth: boolean;
  isWeekStart: boolean;
  isWeekEnd: boolean;
};

export type MonthCalendarSelection =
  | { type: 'week'; weekStart: Date }
  | { type: 'month'; monthStart: Date };

export const buildMonthCalendarDays = (
  visibleMonth: Date,
  selection: MonthCalendarSelection,
): MonthCalendarDay[] => {
  const monthStart = startOfMonth(visibleMonth);
  const gridStart = startOfWeekMonday(monthStart);
  const todayKey = toDateKey(new Date());
  const weekStart = selection.type === 'week' ? selection.weekStart : startOfWeekMonday(selection.monthStart);
  const selectedWeekKeys = new Set(
    Array.from({ length: 7 }, (_, index) => toDateKey(addDays(weekStart, index))),
  );
  const weekStartKey = toDateKey(weekStart);
  const weekEndKey = toDateKey(addDays(weekStart, 6));
  const selectedMonth = selection.type === 'month' ? selection.monthStart.getMonth() : null;
  const selectedYear = selection.type === 'month' ? selection.monthStart.getFullYear() : null;

  return Array.from({ length: 42 }, (_, index) => {
    const date = addDays(gridStart, index);
    const dayKey = toDateKey(date);
    const isInSelectedMonth = selectedMonth !== null
      && date.getMonth() === selectedMonth
      && date.getFullYear() === selectedYear;

    return {
      date,
      dayKey,
      dayNumber: date.getDate(),
      isCurrentMonth: date.getMonth() === visibleMonth.getMonth(),
      isToday: dayKey === todayKey,
      isInSelectedWeek: selectedWeekKeys.has(dayKey),
      isInSelectedMonth,
      isWeekStart: dayKey === weekStartKey,
      isWeekEnd: dayKey === weekEndKey,
    };
  });
};

export const getWeekDays = (
  weekStart: Date,
  t?: RosterDateLabelTranslator,
): RosterWeekDay[] => (
  Array.from({ length: 7 }, (_, index) => {
    const date = addDays(weekStart, index);
    return {
      date,
      dayKey: toDateKey(date),
      dayLabel: t
        ? formatWeekDayColumnLabel(date, index, t)
        : formatDate(date, { weekday: 'short', day: 'numeric', month: 'short' }),
      ...buildDayHeaderParts(date, index, t),
      dayIndex: date.getDay(),
    };
  })
);

export const getMonthDays = (
  monthStart: Date,
  t?: RosterDateLabelTranslator,
): RosterWeekDay[] => {
  const start = startOfMonth(monthStart);
  const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();

  return Array.from({ length: daysInMonth }, (_, index) => {
    const date = addDays(start, index);
    const weekdayIndex = (date.getDay() + 6) % 7;

    return {
      date,
      dayKey: toDateKey(date),
      dayLabel: t
        ? formatMonthDayColumnLabel(date, weekdayIndex, t)
        : formatDate(date, { weekday: 'short', day: 'numeric' }),
      ...buildDayHeaderParts(date, weekdayIndex, t),
      dayIndex: date.getDay(),
    };
  });
};

export const normalizeDaysOfWeek = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalized = value
    .map((item) => {
      if (typeof item === 'number' && Number.isFinite(item)) {
        return Math.max(0, Math.min(6, Math.trunc(item)));
      }

      if (typeof item === 'string') {
        const numeric = Number(item);
        if (Number.isFinite(numeric)) {
          return Math.max(0, Math.min(6, Math.trunc(numeric)));
        }

        return DAY_NAME_TO_INDEX[item.trim().toLowerCase()];
      }

      return undefined;
    })
    .filter((item): item is number => item !== undefined);

  return Array.from(new Set(normalized)).sort((left, right) => left - right);
};

export const formatTimeValue = (value?: string): string => {
  if (!value) {
    return '';
  }

  return value.length >= 5 ? value.slice(0, 5) : value;
};

export const DAY_INDEX_TO_KEY: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

export const excludeDayFromList = (days: number[], dayIndex: number): number[] => (
  days.filter((day) => day !== dayIndex)
);

export const applyRosterSaveResult = (
  current: RosterScheduleRule[],
  result: { rules: RosterScheduleRule[]; removedRuleIds: string[] },
): RosterScheduleRule[] => {
  const withoutRemoved = current.filter((rule) => !result.removedRuleIds.includes(rule.id));
  const withoutUpdated = withoutRemoved.filter(
    (rule) => !result.rules.some((saved) => saved.id === rule.id),
  );

  return [...withoutUpdated, ...result.rules].sort((left, right) => left.type.localeCompare(right.type));
};

export const formatShiftLabel = (
  rule: Pick<RosterScheduleRule, 'startTime' | 'endTime' | 'isAllDay' | 'scheduleStatus'>,
  statusLabel?: string,
): string => {
  const timeLabel = (() => {
    if (rule.isAllDay) {
      return 'All day';
    }

    const startTime = formatTimeValue(rule.startTime);
    const endTime = formatTimeValue(rule.endTime);

    if (startTime && endTime) {
      return `${startTime} – ${endTime}`;
    }

    return startTime || endTime || '—';
  })();

  if (statusLabel && rule.scheduleStatus !== 'working') {
    return `${statusLabel} · ${timeLabel}`;
  }

  return timeLabel;
};

const isDateWithinRange = (dateKey: string, startDate?: string, endDate?: string): boolean => {
  if (startDate && dateKey < startDate) {
    return false;
  }

  if (endDate && dateKey > endDate) {
    return false;
  }

  return true;
};

export const ruleAppliesToWeekDay = (rule: RosterScheduleRule, day: RosterWeekDay): boolean => {
  if (!rule.isEnabled) {
    return false;
  }

  if (rule.startAtUtc && rule.endAtUtc) {
    const startAt = new Date(rule.startAtUtc);
    const endAt = new Date(rule.endAtUtc);
    if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
      return false;
    }

    const dayStart = new Date(day.date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day.date);
    dayEnd.setHours(23, 59, 59, 999);
    return startAt <= dayEnd && endAt >= dayStart;
  }

  if (!rule.daysOfWeek.includes(day.dayIndex)) {
    return false;
  }

  return isDateWithinRange(day.dayKey, rule.startDate, rule.endDate);
};

export type RosterOverviewKind = 'schedule' | 'onCall' | 'all';

export const buildRosterCellMap = (
  rules: RosterScheduleRule[],
  weekDays: RosterWeekDay[],
  employeeIds: string[],
  overviewKind: RosterOverviewKind = 'all',
): Record<string, RosterCellEntry[]> => {
  const scopedRules = overviewKind === 'all'
    ? rules
    : rules.filter((rule) => (
      overviewKind === 'schedule' ? rule.type === 'staffShift' : rule.type === 'staffTimeOff'
    ));

  const cells: Record<string, RosterCellEntry[]> = {};

  for (const employeeId of employeeIds) {
    for (const day of weekDays) {
      cells[`${employeeId}|${day.dayKey}`] = [];
    }
  }

  for (const rule of scopedRules) {
    if (!rule.staffId) {
      continue;
    }

    for (const day of weekDays) {
      if (!ruleAppliesToWeekDay(rule, day)) {
        continue;
      }

      const key = `${rule.staffId}|${day.dayKey}`;
      if (!cells[key]) {
        cells[key] = [];
      }

      cells[key].push({
        ruleId: rule.id,
        type: rule.type,
        label: formatShiftLabel(rule),
        isEnabled: rule.isEnabled,
      });
    }
  }

  for (const key of Object.keys(cells)) {
    cells[key].sort((left, right) => left.label.localeCompare(right.label));
  }

  return cells;
};
